import { useSyncExternalStore } from 'react';

/**
 * Which sidebar categories the reader folded away.
 *
 * A module store rather than component state because `<Sidebar>` is mounted
 * twice at once — the desktop rail and the mobile drawer, whose panel stays in
 * the DOM once opened. Two `useState` copies would drift the moment the reader
 * collapsed a category on one of them.
 *
 * We persist the *collapsed* set, not the open one: a category nobody has ever
 * touched — including one added in a later release — is absent from storage and
 * therefore expanded, which is the behaviour we want by default.
 */
const STORAGE_KEY = 'bill-alps-sidebar-collapsed-groups';

/** Shared so `useSyncExternalStore` sees a stable server snapshot. */
const EMPTY: ReadonlySet<string> = Object.freeze(new Set<string>());

/**
 * `null`, hand-edited junk and older shapes all mean "nothing collapsed" rather
 * than a sidebar that fails to render.
 */
export function parseCollapsedGroups(raw: string | null): ReadonlySet<string> {
  if (!raw) return EMPTY;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    return new Set(
      parsed.filter((key): key is string => typeof key === 'string'),
    );
  } catch {
    return EMPTY;
  }
}

function read(): ReadonlySet<string> {
  if (typeof window === 'undefined') return EMPTY;
  try {
    return parseCollapsedGroups(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return EMPTY;
  }
}

let collapsed = read();
const listeners = new Set<() => void>();

/**
 * Folds or unfolds one category. Always publishes a *new* `Set`: snapshots
 * handed to `useSyncExternalStore` have to be referentially stable between
 * changes and referentially different across them.
 */
export function toggleSidebarGroup(key: string) {
  const next = new Set(collapsed);
  if (!next.delete(key)) next.add(key);
  collapsed = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // Private-mode quota or blocked storage: the fold still holds for the rest
    // of this page's life, it just won't survive a reload.
  }
  for (const listener of listeners) listener();
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function useCollapsedSidebarGroups(): ReadonlySet<string> {
  return useSyncExternalStore(
    subscribe,
    () => collapsed,
    () => EMPTY,
  );
}
